<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon,
  Lock03Icon,
  LockUnlocked03Icon
} from 'front-library'
import {
  generateReportList,
  getReportRequest
} from '@/app/core/services/reports'
import {
  getLoans,
  Loan,
  deleteLoan,
  updateStateLoan
} from '@/app/core/services/loans'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const acceptQuestion = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const tableLoans = ref(null)
const loanTypeForDelete = ref()
const loanUpdateState = ref()
const isDelete = ref(false)
const urlFile = ref(null)
const refresh = ref()
const payload = ref()

/* Datos componente table */
const columns = ref([
  {
    name: 'payrollName',
    title: t('loans.list.payroll'),
    table: 'payrolls.name'
  },
  {
    name: 'loanType',
    title: t('loans.list.loanType'),
    table: 'loan_types.name'
  },

  {
    name: 'conceptName',
    title: t('loans.list.concept'),
    table: 'concepts.name'
  },
  {
    name: 'employeeName',
    title: t('loans.list.employee'),
    table: 'employees.name'
  },
  {
    name: 'currencyName',
    title: t('loans.list.currency'),
    table: 'currencies.name'
  },
  {
    name: 'start_date',
    title: t('loans.list.initialDate'),
    table: 'loan_types.start_date'
  },
  {
    name: 'is_loan_paid',
    title: t('loans.list.loanPaid'),
    table: 'is_loan_paid'
  },
  { name: 'state', title: t('loans.list.status'), table: 'is_inactive' },
  {
    name: 'updated_at',
    title: t('loans.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<Loan[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createLoanType = () => {
  void router.push({ name: 'CreateLoan' })
}
const updateLoanType = (id: any) => {
  void router.push({ name: 'UpdateLoan', params: { id } })
}
const getLoanType = (id: any) => {
  void router.push({ name: 'GetLoan', params: { id } })
}

/* Cambiar estado del prestamo */
const changeStateLoan = (values: any) => {
  const text = values.is_inactive
    ? t('table.action.activate')
    : t('table.action.deactivate')
  titleModal.value = t('loans.list.actionStatus', {
    status: text
  })
  descriptionModal.value = ''
  typeAlertModal.value = 'question'
  acceptQuestion.value = t('modal.acceptQuestion', { action: text })
  loanUpdateState.value = [
    { name: values.name, id: values.id, is_inactive: values.is_inactive }
  ]
  showModal.value = true
  isDelete.value = false
}
const changeStateConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await updateStateLoan(
    loanUpdateState.value[0].id,
    !loanUpdateState.value[0].is_inactive
  )
    .then(response => {
      notifications.addNotification({
        title: !loanUpdateState.value[0].is_inactive
          ? t('notification.disabled')
          : t('notification.activated'),
        text: t('loans.notification.changeStatus', {
          status: !loanUpdateState.value[0].is_inactive
            ? t('table.action.deactivated')
            : t('table.action.asset')
        }),
        type: 'success',
        time: 10000
      })
      void loans()
    })
    .catch((error: any) => {
      notificationError(error, 'loans', t, notifications, loading)
    })
}

/* Eliminar préstamo */
const removeLoanType = (values: any) => {
  titleModal.value = t('loans.list.actionStatus', {
    status: t('table.action.delete')
  })
  descriptionModal.value = t('modal.description')
  typeAlertModal.value = 'question'
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('table.action.delete')
  })
  showModal.value = true
  loanTypeForDelete.value = values.id
  isDelete.value = true
}
const deleteConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await deleteLoan(loanTypeForDelete.value)
    .then(response => {
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('loans.notification.action', [t('notification.wasRemoved')]),
        type: 'success',
        time: 10000
      })
      void loans()
    })
    .catch((error: any) => {
      notificationError(error, 'loans', t, notifications, loading)
    })
}

/* Generar reporte */
const downloadList = async () => {
  try {
    loading.value = true
    messageLoading.value = t('loading.report')
    urlFile.value = null
    const data = {
      code: 'configurations/entity-table',
      parameters: {
        query: JSON.stringify(payload.value),
        fields: payload.value.fields.filter(
          (field: any) => !field.includes('loans.id')
        ),
        headers: columns.value
          .filter((item: any) => item.title !== '')
          .map((item: any) => item.title),
        module: t('loans.list.title', 1).replace('é', 'e')
      }
    }
    const id = await generateReportList(data)
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    notificationError(error, 'loans', t, notifications, loading)
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  urlFile.value = null
}

/* Listar préstamo */
const loans = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getLoans(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data[0].results
    totalRows.value = data[0].totalItems
    payload.value = data[1]
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'loans', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="isDelete ? deleteConfirm() : changeStateConfirm()">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('reports.notification.viewFile')" />
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('loans.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('loans.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="loans" />
          <DBtn @click.prevent="loans" v-html="t('form.button.search')" />
        </div>
        <div class="flex gap-2">
          <DBtn
            color="excel"
            type="button"
            @click="downloadList"
            v-html="t('form.button.download')" />
          <DBtn
            v-if="store.hasPermission('loan:create')"
            :icon="'PlusIcon'"
            type="button"
            @click="createLoanType()">
            {{ t('form.button.create', { form: t('loans.list.title', 0) }) }}
          </DBtn>
        </div>
      </div>
    </div>
    <DTable
      ref="tableLoans"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), loans()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), loans()">
      <template #state="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.is_inactive
              ? 'bg-red-100 text-red-600'
              : 'bg-green-100 text-green-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="data.is_inactive ? 'bg-red-600' : 'bg-green-600'"></div>
          {{
            data.is_inactive
              ? t('table.action.inactive')
              : t('table.action.active')
          }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('loan:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getLoanType(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="!data.is_inactive && store.hasPermission('loan:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateLoanType(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('loan:enable')"
            arrow
            offsetDistance="12"
            :content="
              data.is_inactive
                ? t('loans.list.activate')
                : t('loans.list.deactivate')
            "
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="changeStateLoan(data)">
              <Lock03Icon
                v-if="data.is_inactive"
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              <LockUnlocked03Icon
                v-else
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="data.is_inactive && store.hasPermission('loan:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeLoanType(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
